/* Editable project sketches: Excalidraw-backed feedback canvas. */
(function () {
  const { useEffect, useMemo, useRef, useState } = React;

  function ProjectSketchPanel({ project, active, sketches, onAction, onZenChange }) {
    const ordered = useMemo(() => (sketches || []).slice().sort(sortPositionTitle), [sketches]);
    const workPackages = useMemo(() => (project.workPackages || []).slice().sort(sortPositionTitle), [project.workPackages]);
    const [selectedSketchId, setSelectedSketchId] = useState(() => (ordered[0] && ordered[0].id) || '');
    const selectedSketch = ordered.find((item) => item.id === selectedSketchId) || null;
    const [draft, setDraft] = useState(() => parseScene(selectedSketch));
    const [draftSource, setDraftSource] = useState(() => sketchSignature(selectedSketch));
    const [description, setDescription] = useState(() => selectedSketch && selectedSketch.description || '');
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [savingExport, setSavingExport] = useState(false);
    const [fullscreen, setFullscreen] = useState(false);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [sketchMenuOpen, setSketchMenuOpen] = useState(false);
    const lastSavedRef = useRef(stringifyScene(draft));
    const draftCacheRef = useRef({});
    const hydratingCachedSketchRef = useRef('');

    useEffect(() => {
      setSelectedSketchId((current) => {
        if (current && ordered.some((item) => item.id === current)) return current;
        return (ordered[0] && ordered[0].id) || '';
      });
    }, [ordered.map((item) => item.id).join('|')]);

    useEffect(() => {
      const cache = selectedSketch ? draftCacheRef.current[selectedSketch.id] : null;
      const source = sketchSignature(selectedSketch);
      const hasCachedDraft = !!(cache && cache.source === source);
      const next = hasCachedDraft ? cache.scene : parseScene(selectedSketch);
      const saved = stringifyScene(parseScene(selectedSketch));
      hydratingCachedSketchRef.current = hasCachedDraft && selectedSketch ? selectedSketch.id : '';
      setDraft(next);
      setDraftSource(source);
      setDescription(hasCachedDraft ? cache.description : selectedSketch && selectedSketch.description || '');
      setDirty(stringifyScene(next) !== saved);
      setSketchMenuOpen(false);
      lastSavedRef.current = saved;
    }, [selectedSketch && selectedSketch.id, selectedSketch && selectedSketch.sketchJson, selectedSketch && selectedSketch.description]);

    useEffect(() => {
      if (!fullscreen) return;
      const onKey = (event) => {
        if (event.key === 'Escape') setFullscreen(false);
      };
      document.body.classList.add('sketch-fullscreen-open');
      window.addEventListener('keydown', onKey);
      return () => {
        document.body.classList.remove('sketch-fullscreen-open');
        window.removeEventListener('keydown', onKey);
      };
    }, [fullscreen]);

    useEffect(() => {
      if (onZenChange) onZenChange(fullscreen);
      return () => { if (onZenChange) onZenChange(false); };
    }, [fullscreen]);

    const elementCount = Array.isArray(draft.elements) ? draft.elements.filter((item) => !item.isDeleted).length : 0;
    const fileCount = draft.files && typeof draft.files === 'object' ? Object.keys(draft.files).length : 0;
    const descriptionDirty = selectedSketch ? description !== (selectedSketch.description || '') : false;
    const hasUnsavedChanges = dirty || descriptionDirty;

    const sketchBody = (sketch, nextScene, workPackageId, feedbackJson, nextDescription) => ({
      workPackageId: workPackageId || sketch.workPackageId || active.id,
      title: sketch.title || `${active.name || project.name || 'App'} sketch`,
      description: nextDescription !== undefined ? nextDescription : sketch.description || '',
      prompt: sketch.prompt || '',
      sketchJson: stringifyScene(nextScene),
      feedbackJson: feedbackJson !== undefined ? feedbackJson : sketch.feedbackJson || ''
    });

    const cacheDraft = (nextScene, nextDescription) => {
      if (!selectedSketch) return;
      draftCacheRef.current[selectedSketch.id] = {
        source: sketchSignature(selectedSketch),
        scene: nextScene,
        description: nextDescription
      };
    };

    const save = async (nextScene = draft, feedbackJson, options = {}) => {
      if (!selectedSketch) return false;
      setSaving(true);
      try {
        const ok = await onAction('updateProjectSketch', {
          projectId: project.id,
          sketchId: selectedSketch.id,
          sketch: sketchBody(selectedSketch, nextScene, selectedSketch.workPackageId || active.id, feedbackJson, description),
          silent: options.silent !== undefined ? options.silent : true
        });
        if (ok !== false) {
          const saved = stringifyScene(nextScene);
          lastSavedRef.current = saved;
          delete draftCacheRef.current[selectedSketch.id];
          setDirty(false);
        }
        return ok !== false;
      } finally {
        setSaving(false);
      }
    };

    const saveIfNeeded = async () => {
      if (!selectedSketch || !hasUnsavedChanges) return true;
      return save(draft, undefined, { silent: true });
    };

    const selectSketch = async (sketchId) => {
      if (!sketchId || sketchId === selectedSketchId || saving) return;
      const saved = await saveIfNeeded();
      if (saved === false) return;
      setSelectedSketchId(sketchId);
      setSketchMenuOpen(false);
    };

    const onSceneChange = (scene, options = {}) => {
      const next = normalizeScene(scene);
      setDraft(next);
      const serialized = stringifyScene(next);
      if (options.initial) {
        if (selectedSketch && hydratingCachedSketchRef.current === selectedSketch.id) {
          hydratingCachedSketchRef.current = '';
          cacheDraft(next, description);
          setDirty(serialized !== lastSavedRef.current);
        } else {
          lastSavedRef.current = serialized;
          setDirty(false);
        }
      } else {
        cacheDraft(next, description);
        setDirty(serialized !== lastSavedRef.current);
      }
    };

    const updateDescription = (nextDescription) => {
      setDescription(nextDescription);
      cacheDraft(draft, nextDescription);
    };

    const buildStandaloneExport = async () => {
      if (!selectedSketch) throw new Error('No sketch selected.');
      const bridge = await getExcalidrawBridge();
      if (!bridge.exportSceneToSvgText) throw new Error('Excalidraw export is unavailable.');
      const title = selectedSketch.title || `${active.name || project.name || 'App'} sketch`;
      const fileName = `${slugifyFileName(title)}-sketch.html`;
      const sketchWorkPackage = workPackages.find((item) => item.id === selectedSketch.workPackageId) || active;
      const svgText = await bridge.exportSceneToSvgText(draft, title);
      return {
        title,
        fileName,
        html: standaloneSketchHtml({
          title,
          projectName: project.name || '',
          workPackageName: sketchWorkPackage && sketchWorkPackage.name || '',
          description,
          elementCount,
          exportedAt: new Date(),
          svgText
        })
      };
    };

    const exportStandaloneSketch = async () => {
      if (!selectedSketch || exporting) return;
      setExporting(true);
      try {
        const payload = await buildStandaloneExport();
        downloadTextFile(payload.fileName, payload.html, 'text/html;charset=utf-8');
      } catch (error) {
        window.alert(error && error.message ? error.message : 'Could not export this sketch.');
      } finally {
        setExporting(false);
      }
    };

    const saveStandaloneSketchFile = async () => {
      if (!selectedSketch || savingExport) return;
      setSavingExport(true);
      try {
        const saved = await saveIfNeeded();
        if (saved === false) return false;
        const title = selectedSketch.title || `${active.name || project.name || 'App'} sketch`;
        const ok = await onAction('saveProjectSketchStandaloneExport', {
          projectId: project.id,
          sketchId: selectedSketch.id,
          export: {
            fileName: `${slugifyFileName(title)}-sketch.html`,
            description: `Standalone customer export for sketch "${title}".`
          }
        });
        return ok !== false;
      } catch (error) {
        window.alert(error && error.message ? error.message : 'Could not save this sketch export.');
        return false;
      } finally {
        setSavingExport(false);
      }
    };

    const createSketch = () => onAction('createProjectSketch', {
      projectId: project.id,
      sketch: {
        workPackageId: active.id,
        title: `${active.name || project.name} sketch`,
        description: '',
        prompt: '',
        sketchJson: stringifyScene(defaultSketch(project, active)),
        feedbackJson: ''
      }
    });

    const deleteSketch = (sketch) => {
      if (!sketch) return;
      if (!window.confirm(`Delete "${sketch.title || 'Sketch'}"? This removes the sketch canvas and its latest feedback.`)) return;
      onAction('deleteProjectSketch', { projectId: project.id, sketchId: sketch.id, title: sketch.title || 'this sketch' });
    };

    const switchSketchWorkPackage = async (sketch, targetWorkPackageId) => {
      if (!sketch || !targetWorkPackageId || sketch.workPackageId === targetWorkPackageId || saving) return;
      const currentScene = selectedSketch && selectedSketch.id === sketch.id ? draft : parseScene(sketch);
      const currentDescription = selectedSketch && selectedSketch.id === sketch.id ? description : sketch.description || '';
      if (selectedSketch && selectedSketch.id === sketch.id) {
        const saved = await saveIfNeeded();
        if (saved === false) return;
      }

      setSaving(true);
      try {
        const ok = await onAction('updateProjectSketch', {
          projectId: project.id,
          sketchId: sketch.id,
          sketch: sketchBody(sketch, currentScene, targetWorkPackageId, undefined, currentDescription),
          silent: false
        });
        if (ok !== false && selectedSketch && selectedSketch.id === sketch.id) {
          const nextSketch = ordered.find((item) => item.id !== sketch.id);
          setSelectedSketchId((nextSketch && nextSketch.id) || '');
        }
      } finally {
        setSaving(false);
      }
    };

    const sceneKey = selectedSketch ? `${selectedSketch.id}:${selectedSketch.updatedAt || selectedSketch.sketchJson || ''}` : 'empty';
    const selectedSource = sketchSignature(selectedSketch);
    const canvasScene = selectedSketch && draftSource === selectedSource ? draft : parseScene(selectedSketch);

    return (
      <div className={'sketch-studio' + (fullscreen ? ' zen' : '')}>
        {!fullscreen && (
          <div className="sketch-studio-head">
            <div className="sketch-picker sketch-head-picker">
              <button className="sketch-picker-btn" disabled={!ordered.length} onClick={() => setSketchMenuOpen((value) => !value)}>
                <span className="sketch-picker-ico"><Icon name="flow" size={14} /></span>
                <span>{selectedSketch ? selectedSketch.title || 'Sketch' : 'No sketches'}</span>
                <Icon name="chevD" size={12} />
              </button>
              {sketchMenuOpen && (
                <div className="sketch-picker-menu">
                  {ordered.length ? ordered.map((sketch, index) => (
                    <div
                      key={sketch.id}
                      className={'sketch-picker-row ' + (selectedSketch && sketch.id === selectedSketch.id ? 'on' : '')}
                    >
                      <button type="button" className="sketch-picker-row-main" onClick={() => selectSketch(sketch.id)}>
                        <span className="dot" />
                        <span className="copy"><strong>{sketch.title || 'Sketch'}</strong><small>{sketch.feedbackJson ? 'Feedback ready' : 'Excalidraw scene'}</small></span>
                        {selectedSketch && sketch.id === selectedSketch.id ? <Icon name="check" size={13} /> : null}
                      </button>
                      <span className="row-actions">
                        <button type="button" title="Move sketch up" disabled={index <= 0} onClick={() => onAction('reorderProjectSketch', { project, sketchId: sketch.id, direction: -1 })}><Icon name="arrowUp" size={12} /></button>
                        <button type="button" title="Move sketch down" disabled={index === ordered.length - 1} onClick={() => onAction('reorderProjectSketch', { project, sketchId: sketch.id, direction: 1 })}><Icon name="arrowDown" size={12} /></button>
                        <button type="button" title="Delete sketch" onClick={() => deleteSketch(sketch)}><Icon name="trash" size={12} /></button>
                      </span>
                    </div>
                  )) : <div className="sketch-picker-empty">No sketches yet.</div>}
                  <button className="sketch-picker-new" onClick={createSketch}><Icon name="plus" size={13} />New sketch</button>
                </div>
              )}
            </div>
            <div className="sketch-studio-title">
              <h2>App sketch - {selectedSketch ? selectedSketch.title || 'Untitled sketch' : active.name || 'Work package'}</h2>
              <div className="sub">Excalidraw canvas synced to {active.name || 'selected work package'}</div>
            </div>
            <div className="actions">
              {workPackages.length > 1 && (
                <select className="inp sm sketch-work-package-select" aria-label="Switch sketch work package" value={selectedSketch ? selectedSketch.workPackageId || active.id : active.id} disabled={!selectedSketch || saving} onChange={(e) => switchSketchWorkPackage(selectedSketch, e.target.value)}>
                  {workPackages.map((workPackage) => <option key={workPackage.id} value={workPackage.id}>{workPackage.name || 'Untitled work package'}</option>)}
                </select>
              )}
              {hasUnsavedChanges ? <span className="chip">Unsaved</span> : <span className="chip">Saved</span>}
              <button className={'btn sm ghost' + (detailsOpen ? ' primary' : '')} disabled={!selectedSketch} aria-expanded={detailsOpen} onClick={() => setDetailsOpen((value) => !value)}><Icon name="flow" size={13} />Sketch details</button>
              <button className="btn sm ghost" disabled={!selectedSketch || exporting} onClick={exportStandaloneSketch}><Icon name="download" size={13} />{exporting ? 'Exporting...' : 'Export'}</button>
              <button className="btn sm ghost" disabled={!selectedSketch || savingExport} onClick={saveStandaloneSketchFile}><Icon name="fileImage" size={13} />{savingExport ? 'Saving...' : 'Save to files'}</button>
              <button className="btn sm ghost" onClick={() => setFullscreen(true)}><Icon name="maximize" size={13} />Full screen</button>
              <button className="btn sm ghost" disabled={!selectedSketch || saving} onClick={() => save(draft, undefined, { silent: false })}><Icon name="check" size={13} />{saving ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        )}

        <div className="sketch-studio-workspace">
          {selectedSketch ? (
            <React.Fragment>
              <div className="sketch-studio-main">
                <div className="sketch-canvas-slot">
                  <ExcalidrawHost
                    sceneKey={sceneKey}
                    scene={canvasScene}
                    name={selectedSketch.title || `${active.name || project.name || 'App'} sketch`}
                    onChange={onSceneChange}
                  />
                </div>
                {!fullscreen && detailsOpen && (
                <aside className="sketch-side-panel" aria-label="Sketch details">
                  <div className="sketch-side-panel-head">
                    <strong>Sketch details</strong>
                    <span className="chip">{elementCount} element{elementCount === 1 ? '' : 's'}</span>
                  </div>
                  <div className="sketch-details-body">
                    <div className="field">
                      <label>Flow and functions</label>
                      <textarea className="inp" rows="5" value={description} onChange={(e) => updateDescription(e.target.value)} placeholder="Describe the screens, user flow, functions, and important states this sketch covers." />
                    </div>
                    <div className="sketch-feedback-meta">
                      <span className="chip">{fileCount} file{fileCount === 1 ? '' : 's'}</span>
                      <span className="chip">{hasUnsavedChanges ? 'Unsaved' : 'Saved'}</span>
                    </div>
                  </div>
                </aside>
                )}
              </div>

              {fullscreen && <button className="sketch-exit-zen" onClick={() => setFullscreen(false)}><Icon name="minimize" size={13} />Exit full screen</button>}
            </React.Fragment>
          ) : (
            <div className="sketch-empty-state">
              <span className="muted">Create a sketch to start the editable design feedback loop.</span>
              <button className="btn sm" onClick={createSketch}><Icon name="plus" size={13} />New sketch</button>
            </div>
          )}
        </div>
      </div>
    );
  }

  function ExcalidrawHost({ sceneKey, scene, name, onChange, onSelectionChange }) {
    const hostRef = useRef(null);
    const bridgeRef = useRef(null);
    const onChangeRef = useRef(onChange);
    const onSelectionChangeRef = useRef(onSelectionChange);
    const [error, setError] = useState('');
    const [mounted, setMounted] = useState(false);
    onChangeRef.current = onChange;
    onSelectionChangeRef.current = onSelectionChange;

    useEffect(() => {
      let cancelled = false;
      let mounted = null;
      setError('');
      setMounted(false);
      getExcalidrawBridge()
        .then((bridge) => {
          if (cancelled || !hostRef.current) return;
          mounted = bridge.mount(hostRef.current, {
            scene,
            name,
            onChange: (nextScene, options) => onChangeRef.current(nextScene, options),
            onSelectionChange: (elementIds) => {
              if (onSelectionChangeRef.current) onSelectionChangeRef.current(elementIds);
            }
          });
          bridgeRef.current = mounted;
          setMounted(true);
        })
        .catch((err) => {
          if (!cancelled) setError(err && err.message ? err.message : 'Could not load Excalidraw.');
        });
      return () => {
        cancelled = true;
        const current = mounted || bridgeRef.current;
        if (current && current.unmount) current.unmount();
        if (bridgeRef.current === current) bridgeRef.current = null;
      };
    }, [sceneKey]);

    return (
      <div className="sketch-excalidraw-host">
        <div className="sketch-excalidraw-mount" ref={hostRef} />
        {error ? <div className="sketch-loader-state neg">{error}</div> : (!mounted ? <div className="sketch-loader-state">Loading Excalidraw...</div> : null)}
      </div>
    );
  }

  function getExcalidrawBridge() {
    if (window.ExcalidrawSketchReady) return window.ExcalidrawSketchReady;
    return new Promise((resolve, reject) => {
      const timeout = window.setTimeout(() => reject(new Error('Excalidraw loader did not start.')), 12000);
      window.addEventListener('excalidraw-sketch-ready', () => {
        window.clearTimeout(timeout);
        resolve(window.ExcalidrawSketch);
      }, { once: true });
      window.addEventListener('excalidraw-sketch-error', (event) => {
        window.clearTimeout(timeout);
        reject(new Error(event.detail || 'Excalidraw failed to load.'));
      }, { once: true });
    });
  }

  function parseScene(record) {
    if (!record || !record.sketchJson) return defaultSketch();
    try {
      return normalizeScene(JSON.parse(record.sketchJson || '{}'));
    } catch (_) {
      return defaultSketch();
    }
  }

  function defaultSketch(project, active) {
    return normalizeScene({
      type: 'excalidraw',
      source: 'Appligator',
      elements: [],
      appState: {
        name: `${(active && active.name) || (project && project.name) || 'App'} sketch`,
        viewBackgroundColor: '#ffffff'
      },
      files: {}
    });
  }

  function normalizeScene(scene) {
    const value = scene && typeof scene === 'object' && !Array.isArray(scene) ? scene : {};
    return {
      type: value.type || 'excalidraw',
      version: value.version || 2,
      source: value.source || 'Appligator',
      elements: Array.isArray(value.elements) ? value.elements : [],
      appState: value.appState && typeof value.appState === 'object' && !Array.isArray(value.appState) ? value.appState : {},
      files: value.files && typeof value.files === 'object' && !Array.isArray(value.files) ? value.files : {}
    };
  }

  function stringifyScene(scene) {
    return JSON.stringify(normalizeScene(scene));
  }

  function sketchSignature(sketch) {
    return sketch ? `${sketch.updatedAt || ''}:${sketch.sketchJson || ''}:${sketch.description || ''}` : '';
  }

  function sortPositionTitle(a, b) {
    return (Number(a.position) || 0) - (Number(b.position) || 0)
      || String(a.title || a.name || '').localeCompare(String(b.title || b.name || ''));
  }

  function standaloneSketchHtml({ title, projectName, workPackageName, description, elementCount, exportedAt, svgText }) {
    const safeTitle = escapeHtml(title || 'Sketch');
    const context = [projectName, workPackageName].filter(Boolean).join(' / ');
    const safeContext = escapeHtml(context);
    const exported = exportedAt.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
    const safeDescription = escapeHtml(description || '').replace(/\n/g, '<br>');
    return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>${safeTitle}</title>
  <style>
    :root { color-scheme: light; font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; color: #172033; background: #f6f7f9; }
    * { box-sizing: border-box; }
    body { margin: 0; min-height: 100vh; background: #f6f7f9; }
    main { width: min(1280px, calc(100vw - 40px)); margin: 0 auto; padding: 28px 0 36px; }
    header { display: flex; align-items: flex-end; justify-content: space-between; gap: 24px; margin-bottom: 18px; }
    h1 { margin: 0; font-size: 24px; line-height: 1.15; letter-spacing: 0; color: #101828; }
    .context { margin-top: 6px; color: #667085; font-size: 13px; }
    .meta { color: #667085; font-size: 12px; text-align: right; white-space: nowrap; }
    .stage { background: #fff; border: 1px solid #d9dee8; border-radius: 8px; padding: 18px; box-shadow: 0 12px 28px rgba(16, 24, 40, .08); overflow: auto; }
    .stage svg { display: block; width: 100%; height: auto; }
    .description { margin-top: 18px; padding: 14px 16px; border-left: 3px solid #2856c4; background: #fff; color: #344054; font-size: 14px; line-height: 1.6; }
    @media print {
      body, :root { background: #fff; }
      main { width: 100%; padding: 0; }
      header { margin-bottom: 12px; }
      .stage { box-shadow: none; break-inside: avoid; }
    }
  </style>
</head>
<body>
  <main>
    <header>
      <div>
        <h1>${safeTitle}</h1>
        ${safeContext ? `<div class="context">${safeContext}</div>` : ''}
      </div>
      <div class="meta">${escapeHtml(exported)}<br>${Number(elementCount) || 0} elements</div>
    </header>
    <section class="stage" aria-label="Sketch canvas">
      ${svgText}
    </section>
    ${safeDescription ? `<section class="description">${safeDescription}</section>` : ''}
  </main>
</body>
</html>`;
  }

  function downloadTextFile(fileName, text, type) {
    const blob = new Blob([text], { type });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  function slugifyFileName(value) {
    return String(value || 'sketch')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 80) || 'sketch';
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  Object.assign(window, { ProjectSketchPanel, defaultProjectSketch: defaultSketch });
})();
